@if (announcement(); as item) {
  <div
    class="announcement-bar fixed top-0 left-0 z-60 w-full text-white border-b border-white/20"
    [style.height.rem]="announcementHeightRem"
  >
    <a
      [routerLink]="['/news', item.slug]"
      class="mx-auto flex h-full w-full max-w-[120rem] items-center justify-center gap-2 px-4 text-center font-poppins text-[clamp(0.78rem,1.3vw,1.08rem)] font-medium tracking-[0.01em] no-underline text-white transition-opacity hover:opacity-90"
    >
      <span class="truncate">Important Message: {{ item.title }}</span>
      <span class="hidden underline decoration-2 underline-offset-2 sm:inline">Learn More</span>
    </a>
  </div>
}

@if (happeningEvent(); as event) {
  <div
    class="fixed left-0 z-55 w-full bg-[color-mix(in_srgb,var(--primary-500),black_20%)] text-white border-b border-white/15"
    [style.height.rem]="happeningEventHeightRem"
    [style.top]="happeningEventTop()"
  >
    <a
      [routerLink]="['/trainings', event.slug || event.id]"
      class="mx-auto flex h-full w-full max-w-[120rem] items-center justify-center gap-2 px-4 text-center font-poppins text-[clamp(0.75rem,1.15vw,1rem)] font-medium tracking-[0.01em] no-underline text-white transition-opacity hover:opacity-90"
    >
      <span class="truncate">Happening Today: {{ event.title }}</span>
      <span class="hidden underline decoration-2 underline-offset-2 sm:inline">View Event</span>
    </a>
  </div>
}

<header
  class="fixed top-(--announcement-height) left-0 w-full z-50 flex justify-between items-center gap-4 bg-(--site-surface)/95 backdrop-blur-md shadow-lg border-b border-(--site-border) box-border h-(--header-height) px-(--header-padding) font-poppins text-(--site-text) transition-all duration-300"
  style="--header-padding: clamp(1rem, 4vw, 3rem)"
  [style.--announcement-height]="headerBannerOffset()"
  [style.--header-height]="headerBaseHeight"
>
  <!-- LEFT: Logo -->
  <a href="/" class="block cursor-pointer lg:mr-20">
    <img src="assets/hytec-logo.png" class="h-8 block" alt="Hytec Power Inc" />
  </a>

  <!-- RIGHT (Mobile): Search Icon + Hamburger -->
  <div class="flex items-center gap-4 lg:hidden">
    <button
      class="bg-transparent border-none text-(--site-text) text-xl cursor-pointer p-1 flex items-center justify-center active:scale-95 transition-transform"
      (click)="toggleSearch()"
      aria-label="Toggle Search"
    >
      <i class="bi" [class.bi-search]="!isSearchExpanded" [class.bi-x-lg]="isSearchExpanded"></i>
    </button>

    <button
      class="flex flex-col justify-around w-7 h-7 bg-transparent border-none cursor-pointer p-1 z-1001 shrink-0 focus:outline-2 focus:outline-(--primary-500) group"
      [class.active]="isMobileMenuOpen"
      (click)="toggleMobileMenu()"
      [attr.aria-label]="isMobileMenuOpen ? 'Close menu' : 'Open menu'"
      [attr.aria-expanded]="isMobileMenuOpen"
    >
      <span
        class="w-full h-0.5 bg-(--site-text) rounded-full transition-all duration-300 origin-center group-hover:bg-(--primary-500)"
        [class.translate-y-2]="isMobileMenuOpen"
        [class.rotate-45]="isMobileMenuOpen"
      ></span>
      <span
        class="w-full h-0.5 bg-(--site-text) rounded-full transition-all duration-300 origin-center group-hover:bg-(--primary-500)"
        [class.opacity-0]="isMobileMenuOpen"
        [class.scale-x-0]="isMobileMenuOpen"
      ></span>
      <span
        class="w-full h-0.5 bg-(--site-text) rounded-full transition-all duration-300 origin-center group-hover:bg-(--primary-500)"
        [class.-translate-y-2]="isMobileMenuOpen"
        [class.-rotate-45]="isMobileMenuOpen"
      ></span>
    </button>
  </div>

  <!-- CENTER / RIGHT (Desktop): Navigation -->
  <nav
    [class.translate-y-0]="isMobileMenuOpen"
    [class.opacity-100]="isMobileMenuOpen"
    [class.visible]="isMobileMenuOpen"
    class="fixed top-[calc(var(--header-height)+var(--announcement-height))] left-0 w-full h-[calc(100vh-var(--header-height)-var(--announcement-height))] bg-(--site-surface) -translate-y-full opacity-0 invisible transition-all duration-300 ease-out overflow-y-auto z-999 pb-8 lg:static lg:w-auto lg:h-full lg:bg-transparent lg:translate-y-0 lg:opacity-100 lg:visible lg:overflow-visible lg:p-0 lg:border-none"
  >
    <ul
      class="flex flex-col m-0 p-0 list-none lg:flex-row lg:items-center lg:gap-[clamp(1rem,2vw,2.5rem)] lg:h-full"
    >
      <li
        class="w-full border-b border-(--site-border) lg:border-none lg:w-auto lg:h-full lg:flex lg:items-center lg:relative"
      >
        <a
          routerLink="/"
          routerLinkActive="text-(--primary-500) active-root"
          [routerLinkActiveOptions]="{ exact: true }"
          class="nav-root-link no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500) lg:p-0 lg:text-[clamp(0.75rem,0.8vw,0.9rem)]"
          (click)="onHomeClick($event)"
        >
          HOME
        </a>
      </li>

      <li
        class="solutions-dropdown w-full border-b border-(--site-border) lg:border-none lg:w-auto lg:h-full lg:flex lg:items-center lg:relative group/dropdown"
        [class.solutions-open]="isSolutionsDropdownOpen"
        (mouseenter)="onSolutionsMouseEnter()"
        (mouseleave)="onSolutionsMouseLeave()"
      >
        <button
          type="button"
          (click)="onSolutionsTriggerClick($event)"
          [ngClass]="{
            'text-(--primary-500)': isSolutionsActive() || isSolutionsDropdownOpen,
            'active-root': isSolutionsActive() || isSolutionsDropdownOpen,
          }"
          [attr.aria-expanded]="isSolutionsDropdownOpen"
          aria-haspopup="true"
          class="nav-root-link no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500) lg:p-0 lg:text-[clamp(0.75rem,0.8vw,0.9rem)]"
        >
          PRODUCTS
          <span
            class="transition-transform duration-300 lg:ml-1.5 lg:text-[0.7em]"
            [class.rotate-180]="isSolutionsDropdownOpen"
            >&#9662;</span
          >
        </button>

        <!-- Dropdown Menu -->
        <ul
          class="solutions-submenu max-h-0 overflow-hidden opacity-0 transition-all duration-300 ease-out bg-(--site-surface) p-0 list-none w-full lg:absolute lg:top-[calc(100%+0.1rem)] lg:left-0 lg:translate-y-1 lg:w-max lg:min-w-[20rem] lg:max-h-none lg:overflow-visible lg:bg-(--site-surface) lg:backdrop-blur-md lg:rounded-2xl lg:border lg:border-(--site-border) lg:shadow-2xl lg:p-2 lg:opacity-0 lg:invisible lg:pointer-events-none"
          [class.max-h-screen]="isSolutionsDropdownOpen"
          [class.opacity-100]="isSolutionsDropdownOpen"
        >
          <!-- Desktop Dropdown Bridge -->
          <li aria-hidden="true" class="hidden lg:block absolute -top-4 left-0 right-0 h-4"></li>

          <li class="products-mega hidden lg:grid">
            <div class="products-mega__group">
              <p class="products-mega__label">Educational Solutions</p>
              <a routerLink="/solutions/educational-solution" (click)="onNavLinkClick()">
                <i class="bi bi-grid"></i>
                <span>Overview</span>
              </a>
              <p class="products-mega__sub-label">Learning Paths</p>
              <a routerLink="/products/courses/higher-education" (click)="onNavLinkClick()">
                <i class="bi bi-mortarboard"></i>
                <span>Higher Education</span>
              </a>
              <a routerLink="/products/courses/tvet" (click)="onNavLinkClick()">
                <i class="bi bi-tools"></i>
                <span>TVET</span>
              </a>
              <a routerLink="/products/courses/basic-education" (click)="onNavLinkClick()">
                <i class="bi bi-book"></i>
                <span>Basic Education</span>
              </a>
              <a routerLink="/product-categories" (click)="onNavLinkClick()">
                <i class="bi bi-person-workspace"></i>
                <span>Specialization</span>
              </a>
            </div>

            <div class="products-mega__group">
              <p class="products-mega__label">Industrial Solutions</p>
              <a routerLink="/solutions/industrial-solution" (click)="onNavLinkClick()">
                <i class="bi bi-wrench-adjustable-circle"></i>
                <span>Overview</span>
              </a>
            </div>

            <div class="products-mega__group">
              <p class="products-mega__label">Resources</p>
              <a routerLink="/product-catalogs" (click)="onNavLinkClick()">
                <i class="bi bi-grid-3x3-gap"></i>
                <span>Product Catalogs</span>
              </a>
            </div>
          </li>

          <li class="border-b border-(--site-border) lg:hidden">
            <div
              class="flex items-stretch overflow-hidden lg:rounded-lg hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)]"
            >
              <a
                routerLink="/solutions/educational-solution"
                (click)="onEducationPrimaryClick($event)"
                class="flex min-w-0 flex-1 items-center gap-4 px-10 py-4 no-underline lg:px-4 lg:py-3"
              >
                <i
                  class="bi bi-mortarboard text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"
                ></i>
                <span
                  class="truncate text-base font-semibold text-(--site-text) lg:text-sm whitespace-nowrap"
                  >EDUCATIONAL SOLUTIONS</span
                >
              </a>
              <button
                type="button"
                (click)="onEducationSubmenuToggle($event)"
                class="flex shrink-0 items-center justify-center px-4 text-(--site-text-muted) transition-colors hover:text-(--primary-300)"
                aria-label="Open educational solutions menu"
                [attr.aria-expanded]="isEducationSubmenuOpen"
              >
                <span
                  class="transition-transform duration-300"
                  [class.rotate-180]="isEducationSubmenuOpen && !isDesktop"
                  [class.translate-x-1]="isDesktop"
                  >&#8250;</span
                >
              </button>
            </div>

            <ul
              class="max-h-0 overflow-hidden opacity-0 transition-all duration-300 ease-out bg-[color-mix(in_srgb,var(--site-text),transparent_96%)] list-none mx-4 mb-3 rounded-2xl border border-(--site-border) p-0 lg:hidden"
              [class.max-h-80]="isEducationSubmenuOpen"
              [class.opacity-100]="isEducationSubmenuOpen"
            >
              <li class="group/courses border-b border-(--site-border)">
                <button
                  type="button"
                  (click)="onEducationCoursesToggle($event)"
                  class="flex w-full items-center justify-between gap-4 px-5 py-3 text-left hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] lg:min-h-[3.25rem] lg:rounded-lg"
                >
                  <span class="flex items-center gap-3">
                    <i class="bi bi-journal-bookmark text-lg text-(--primary-300) opacity-80"></i>
                    <span class="text-sm font-semibold text-(--site-text) whitespace-nowrap"
                      >COURSES</span
                    >
                  </span>
                  <span
                    class="text-(--site-text-muted) transition-transform duration-300"
                    [class.rotate-180]="isEducationCoursesSubmenuOpen && !isDesktop"
                    [class.translate-x-1]="isDesktop"
                    >&#8250;</span
                  >
                </button>

                <ul
                  class="max-h-0 overflow-hidden opacity-0 transition-all duration-300 ease-out list-none mx-3 mb-3 rounded-2xl border border-(--site-border) p-0"
                  [class.max-h-80]="isEducationCoursesSubmenuOpen"
                  [class.opacity-100]="isEducationCoursesSubmenuOpen"
                >
                  <li class="border-b border-(--site-border)">
                    <a
                      routerLink="/products/courses/higher-education"
                      (click)="onNavLinkClick()"
                      class="flex items-center gap-3 px-5 py-3 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)]"
                    >
                      <i class="bi bi-mortarboard text-lg text-(--primary-300) opacity-80"></i>
                      <span class="text-sm font-semibold text-(--site-text) whitespace-nowrap"
                        >HIGHER EDUCATION</span
                      >
                    </a>
                  </li>

                  <li class="border-b border-(--site-border)">
                    <a
                      routerLink="/products/courses/tvet"
                      (click)="onNavLinkClick()"
                      class="flex items-center gap-3 px-5 py-3 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)]"
                    >
                      <i class="bi bi-tools text-lg text-(--primary-300) opacity-80"></i>
                      <span class="text-sm font-semibold text-(--site-text) whitespace-nowrap"
                        >TVET</span
                      >
                    </a>
                  </li>

                  <li>
                    <a
                      routerLink="/products/courses/basic-education"
                      (click)="onNavLinkClick()"
                      class="flex items-center gap-3 px-5 py-3 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)]"
                    >
                      <i class="bi bi-book text-lg text-(--primary-300) opacity-80"></i>
                      <span class="text-sm font-semibold text-(--site-text) whitespace-nowrap"
                        >BASIC EDUCATION</span
                      >
                    </a>
                  </li>
                </ul>
              </li>

              <li>
                <a
                  routerLink="/product-categories"
                  (click)="onNavLinkClick()"
                  class="flex items-center gap-3 px-5 py-3 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)]"
                >
                  <i class="bi bi-person-workspace text-lg text-(--primary-300) opacity-80"></i>
                  <span class="text-sm font-semibold text-(--site-text) whitespace-nowrap"
                    >SPECIALIZATION</span
                  >
                </a>
              </li>
            </ul>
          </li>

          <li class="overflow-hidden lg:hidden">
            <a
              routerLink="/solutions/industrial-solution"
              (click)="onNavLinkClick()"
              class="flex items-center gap-4 px-10 py-4 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] lg:px-4 lg:py-3"
            >
              <i
                class="bi bi-wrench-adjustable-circle text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"
              ></i>
              <span class="text-base font-semibold text-(--site-text) lg:text-sm whitespace-nowrap"
                >INDUSTRIAL SOLUTIONS</span
              >
            </a>
          </li>

          <li class="overflow-hidden lg:hidden">
            <a
              routerLink="/product-catalogs"
              (click)="onNavLinkClick()"
              class="flex items-center gap-4 px-10 py-4 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] lg:px-4 lg:py-3"
            >
              <i
                class="bi bi-grid-3x3-gap text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"
              ></i>
              <span class="text-base font-semibold text-(--site-text) lg:text-sm whitespace-nowrap">
                PRODUCT CATALOGS
              </span>
            </a>
          </li>
        </ul>
      </li>

      <li
        class="products-dropdown w-full border-b border-(--site-border) lg:border-none lg:w-auto lg:h-full lg:flex lg:items-center lg:relative group/dropdown"
        [class.products-open]="isProductsDropdownOpen"
        (mouseenter)="onProductsMouseEnter()"
        (mouseleave)="onProductsMouseLeave()"
      >
        <a
          href="#"
          (click)="onProductsTriggerClick($event)"
          [ngClass]="{
            'text-(--primary-500)': isProductsActive() || isProductsDropdownOpen,
            'active-root': isProductsActive() || isProductsDropdownOpen,
          }"
          [attr.aria-expanded]="isProductsDropdownOpen"
          class="nav-root-link no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500) lg:p-0 lg:text-[clamp(0.75rem,0.8vw,0.9rem)]"
        >
          PARTNERS
          <span
            class="transition-transform duration-300 lg:ml-1.5 lg:text-[0.7em]"
            [class.rotate-180]="isProductsDropdownOpen"
            >&#9662;</span
          >
        </a>

        <ul
          class="products-submenu max-h-0 overflow-hidden opacity-0 transition-all duration-300 ease-out bg-(--site-surface) p-0 list-none w-full lg:absolute lg:top-[calc(100%+0.1rem)] lg:left-0 lg:translate-y-1 lg:w-max lg:min-w-[14rem] lg:max-h-none lg:overflow-visible lg:bg-(--site-surface) lg:backdrop-blur-md lg:rounded-2xl lg:border lg:border-(--site-border) lg:shadow-2xl lg:p-2 lg:opacity-0 lg:invisible lg:pointer-events-none"
          [class.max-h-screen]="isProductsDropdownOpen"
          [class.opacity-100]="isProductsDropdownOpen"
        >
          <li aria-hidden="true" class="hidden lg:block absolute -top-4 left-0 right-0 h-4"></li>
          <li class="nav-menu-label hidden lg:block">Partner Network</li>

          <li class="border-b border-(--site-border) lg:border-none lg:rounded-lg overflow-hidden">
            <a
              routerLink="/partners/academe"
              (click)="onNavLinkClick()"
              class="flex items-center gap-4 px-10 py-4 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] lg:px-4 lg:py-3"
            >
              <i
                class="bi bi-mortarboard text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"
              ></i>
              <span class="text-base font-semibold text-(--site-text) lg:text-sm whitespace-nowrap"
                >ACADEME PARTNERS</span
              >
            </a>
          </li>

          <li class="lg:border-none lg:rounded-lg overflow-hidden">
            <a
              routerLink="/partners/industrial"
              (click)="onNavLinkClick()"
              class="flex items-center gap-4 px-10 py-4 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] lg:px-4 lg:py-3"
            >
              <i
                class="bi bi-wrench-adjustable-circle text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"
              ></i>
              <span class="text-base font-semibold text-(--site-text) lg:text-sm whitespace-nowrap"
                >INDUSTRIAL PARTNERS</span
              >
            </a>
          </li>
        </ul>
      </li>

      <li
        class="w-full border-b border-(--site-border) lg:border-none lg:w-auto lg:h-full lg:flex lg:items-center lg:relative"
      >
        <a
          routerLink="/news-and-updates"
          routerLinkActive="text-(--primary-500) active-root"
          class="nav-root-link no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500) lg:p-0 lg:text-[clamp(0.75rem,0.8vw,0.9rem)]"
          (click)="onNavLinkClick()"
        >
          NEWS & UPDATES
        </a>
      </li>

      <li
        class="w-full border-b border-(--site-border) lg:border-none lg:w-auto lg:h-full lg:flex lg:items-center lg:relative"
      >
        <a
          routerLink="/trainings"
          routerLinkActive="text-(--primary-500) active-root"
          class="nav-root-link no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500) lg:p-0 lg:text-[clamp(0.75rem,0.8vw,0.9rem)]"
          (click)="onNavLinkClick()"
        >
          TRAININGS & EVENTS
        </a>
      </li>

      <li
        class="w-full border-b border-(--site-border) lg:border-none lg:w-auto lg:h-full lg:flex lg:items-center lg:relative"
      >
        <a
          routerLink="/virtual-tour"
          routerLinkActive="text-(--primary-500) active-root"
          [routerLinkActiveOptions]="{ exact: true }"
          class="nav-root-link no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500) lg:p-0 lg:text-[clamp(0.75rem,0.8vw,0.9rem)]"
          (click)="onNavLinkClick()"
        >
          VIRTUAL TOUR
        </a>
      </li>

      <li
        class="company-dropdown w-full border-b border-(--site-border) lg:border-none lg:w-auto lg:h-full lg:flex lg:items-center lg:relative group/dropdown"
        [class.company-open]="isCompanyDropdownOpen"
        (mouseenter)="onCompanyMouseEnter()"
        (mouseleave)="onCompanyMouseLeave()"
      >
        <a
          href="#"
          (click)="onCompanyTriggerClick($event)"
          [ngClass]="{
            'text-(--primary-500)': isCompanyActive() || isCompanyDropdownOpen,
            'active-root': isCompanyActive() || isCompanyDropdownOpen,
          }"
          [attr.aria-expanded]="isCompanyDropdownOpen"
          class="nav-root-link no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500) lg:p-0 lg:text-[clamp(0.75rem,0.8vw,0.9rem)]"
        >
          COMPANY
          <span
            class="transition-transform duration-300 lg:ml-1.5 lg:text-[0.7em]"
            [class.rotate-180]="isCompanyDropdownOpen"
            >&#9662;</span
          >
        </a>

        <ul
          class="company-submenu max-h-0 overflow-hidden opacity-0 transition-all duration-300 ease-out bg-(--site-surface) p-0 list-none w-full lg:absolute lg:top-[calc(100%+0.1rem)] lg:left-0 lg:translate-y-1 lg:w-max lg:min-w-[20rem] lg:max-h-none lg:overflow-visible lg:bg-(--site-surface) lg:backdrop-blur-md lg:rounded-2xl lg:border lg:border-(--site-border) lg:shadow-2xl lg:p-2 lg:opacity-0 lg:invisible lg:pointer-events-none"
          [class.max-h-screen]="isCompanyDropdownOpen"
          [class.opacity-100]="isCompanyDropdownOpen"
        >
          <!-- Desktop Dropdown Bridge -->
          <li aria-hidden="true" class="hidden lg:block absolute -top-4 left-0 right-0 h-4"></li>
          <li class="nav-menu-label hidden lg:block">Company</li>

          <li class="border-b border-(--site-border) lg:border-none lg:rounded-lg overflow-hidden">
            <a
              routerLink="/about-us"
              routerLinkActive="active-link"
              #aboutRla="routerLinkActive"
              (click)="onNavLinkClick()"
              class="flex items-center gap-4 px-10 py-4 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] group/item lg:px-4 lg:py-3"
            >
              <i class="bi bi-building text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"></i>
              <span
                class="text-base text-(--site-text) m-0 font-semibold group-hover/item:text-(--primary-500) lg:text-sm whitespace-nowrap"
              >
                ABOUT US
              </span>
            </a>
          </li>
          <li class="border-b border-(--site-border) lg:border-none lg:rounded-lg overflow-hidden">
            <a
              routerLink="/company/sponsorship"
              (click)="onNavLinkClick()"
              class="flex items-center gap-4 px-10 py-4 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] group/item lg:px-4 lg:py-3"
            >
              <i class="bi bi-trophy text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"></i>
              <span
                class="text-base text-(--site-text) m-0 font-semibold group-hover/item:text-(--primary-500) lg:text-sm whitespace-nowrap"
              >
                SPONSORSHIP
              </span>
            </a>
          </li>
          <li class="border-b border-(--site-border) lg:border-none lg:rounded-lg overflow-hidden">
            <a
              routerLink="/company/team"
              (click)="onNavLinkClick()"
              class="flex items-center gap-4 px-10 py-4 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] group/item lg:px-4 lg:py-3"
            >
              <i class="bi bi-people text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"></i>
              <span
                class="text-base text-(--site-text) m-0 font-semibold group-hover/item:text-(--primary-500) lg:text-sm whitespace-nowrap"
              >
                TEAM
              </span>
            </a>
          </li>
          <li class="lg:border-none lg:rounded-lg overflow-hidden">
            <a
              routerLink="/company/our-contacts"
              (click)="onNavLinkClick()"
              class="flex items-center gap-4 px-10 py-4 no-underline hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] group/item lg:px-4 lg:py-3"
            >
              <i class="bi bi-telephone text-xl text-(--primary-300) opacity-80 lg:w-5 lg:h-5"></i>
              <span
                class="text-base text-(--site-text) m-0 font-semibold group-hover/item:text-(--primary-500) lg:text-sm whitespace-nowrap"
              >
                OUR CONTACTS
              </span>
            </a>
          </li>
        </ul>
      </li>

      <li
        class="w-full border-b border-(--site-border) lg:border-none lg:w-auto lg:h-full lg:flex lg:items-center lg:relative"
      >
        <a
          routerLink="/company/careers"
          routerLinkActive="text-(--primary-500) active-root"
          class="nav-root-link no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500) lg:p-0 lg:text-[clamp(0.75rem,0.8vw,0.9rem)]"
          (click)="onNavLinkClick()"
        >
          HIRINGS
        </a>
      </li>

      <li class="w-full border-b border-(--site-border) lg:hidden">
        <a
          href="https://hpimarketplace.com/"
          target="_blank"
          class="no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500)"
          (click)="onNavLinkClick()"
        >
          Marketplace
          <i class="bi bi-cart-fill"></i>
        </a>
      </li>

      @if (!activePublicUser()) {
        <li class="w-full border-b border-(--site-border) lg:hidden">
          <button
            type="button"
            class="no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500)"
            (click)="openPublicAuthModal($event)"
          >
            Login
            <i class="bi bi-box-arrow-in-right"></i>
          </button>
        </li>

        <li class="w-full border-b border-(--site-border) lg:hidden">
          <a
            routerLink="/visitor-signup"
            class="no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500)"
            (click)="onNavLinkClick()"
          >
            Sign Up
            <i class="bi bi-person-plus-fill"></i>
          </a>
        </li>
      }

      <li class="w-full border-b border-(--site-border) lg:hidden">
        <button
          type="button"
          class="border-0 bg-transparent text-left cursor-pointer font-[inherit] no-underline text-(--site-text) text-[clamp(1rem,4vw,1.25rem)] font-medium flex justify-between items-center px-8 py-5 w-full box-border transition-colors hover:text-(--primary-500)"
          (click)="openInquiryDrawer()"
        >
          Get Inquiry
          <i class="bi bi-chat-dots-fill"></i>
        </button>
      </li>

      <!-- Theme Toggle (Mobile Only) -->
      <li class="mobile-theme-item">
        <div
          class="theme-toggle"
          (click)="themeService.toggleTheme()"
          role="button"
          tabindex="0"
          [attr.aria-label]="
            themeService.darkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'
          "
        >
          <div class="theme-toggle__track">
            <div
              class="theme-toggle__thumb"
              [class.translate-x-full]="themeService.darkMode()"
            ></div>
            <div
              class="theme-toggle__icon"
              [style.color]="
                themeService.darkMode() ? 'var(--site-text-muted)' : 'var(--site-surface)'
              "
            >
              <i class="bi bi-cloud-sun"></i>
            </div>
            <div
              class="theme-toggle__icon"
              [style.color]="themeService.darkMode() ? 'var(--site-bg)' : 'var(--site-text-muted)'"
            >
              <i class="bi bi-cloud-moon"></i>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </nav>

  <!-- SEARCH BAR (Collapsible on Mobile, Visible on Desktop) -->
  <div
    class="absolute top-full left-0 w-full bg-(--site-surface) px-(--header-padding) py-4 box-border origin-top scale-y-0 opacity-0 invisible transition-all duration-250 ease-out border-b border-(--site-border) shadow-md lg:relative lg:top-auto lg:left-auto lg:w-40 focus-within:lg:w-[18rem] lg:bg-transparent lg:p-0 lg:border-none lg:shadow-none lg:transform-none lg:scale-y-100 lg:opacity-100 lg:visible lg:block lg:ml-auto lg:transition-[width]"
    [class.scale-y-100]="isSearchExpanded"
    [class.opacity-100]="isSearchExpanded"
    [class.visible]="isSearchExpanded"
  >
    <div
      class="h-10 flex items-center w-full overflow-hidden bg-[color-mix(in_srgb,var(--site-text),transparent_94%)] border border-(--site-border) rounded-2xl px-2 pl-4 box-border lg:h-9 lg:bg-(--site-surface) lg:border-(--site-border) lg:rounded-[1.125rem] hover:lg:border-(--site-text-muted)"
    >
      <input
        type="text"
        placeholder="Search..."
        aria-label="Search"
        [(ngModel)]="searchQuery"
        (ngModelChange)="onSearchInput($event)"
        (focus)="onSearchFocus()"
        (blur)="onSearchBlur()"
        (keyup.enter)="onSearchSubmit()"
        autocomplete="off"
        class="min-w-0 grow bg-transparent border-none text-[var(--site-text)] text-base outline-none lg:text-[clamp(0.75rem,1.5vw,0.875rem)] lg:text-[var(--site-text)] lg:placeholder:text-[var(--site-text-muted)]"
      />
      @if (searchQuery) {
        <button
          type="button"
          class="bg-transparent border-none text-[var(--site-text-muted)] w-8 h-8 shrink-0 flex items-center justify-center outline-none lg:w-7 lg:h-7"
          aria-label="Clear search"
          (click)="clearSearch()"
        >
          <i class="bi bi-x-lg text-xs"></i>
        </button>
      } @else {
        <button
          type="button"
          class="bg-transparent border-none text-[var(--site-text-muted)] w-8 h-8 shrink-0 flex items-center justify-center outline-none lg:w-7 lg:h-7 lg:text-[var(--site-text-muted)]"
          aria-label="Search"
          (click)="onSearchSubmit()"
        >
          <i class="bi bi-search lg:text-sm"></i>
        </button>
      }
    </div>

    @if (showSearchResults) {
      <div
        class="absolute left-(--header-padding) right-(--header-padding) top-[calc(100%-0.25rem)] z-[100] max-h-80 overflow-y-auto rounded-xl border border-(--site-border) bg-(--site-surface) p-2 shadow-2xl lg:left-0 lg:right-0 lg:top-[calc(100%+0.5rem)]"
        aria-live="polite"
      >
        @if (searchLoading) {
          <p class="m-0 px-3 py-4 text-center text-xs text-(--site-text-muted)">Searching...</p>
        } @else {
          @for (result of searchResults; track $index) {
            <a
              [routerLink]="result.route"
              (click)="selectSearchResult()"
              class="flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-left no-underline transition-colors hover:bg-[color-mix(in_srgb,var(--site-text),transparent_94%)]"
            >
              <span class="min-w-0">
                <strong class="block truncate text-sm font-semibold text-(--site-text)">
                  {{ result.title }}
                </strong>
                <small class="mt-0.5 block truncate text-xs text-(--site-text-muted)">
                  {{ result.description }}
                </small>
              </span>
              <span class="shrink-0 text-[0.65rem] uppercase text-(--site-text-muted)">
                {{ result.type }}
              </span>
            </a>
          } @empty {
            <p class="m-0 px-3 py-4 text-center text-xs text-(--site-text-muted)">
              No results found.
            </p>
          }
        }
      </div>
    }
  </div>

  <!-- DESKTOP RIGHT SIDE CTA -->
  <div class="hidden items-center gap-3 lg:flex">
    @if (!activePublicUser()) {
      <button
        type="button"
        (click)="openPublicAuthModal($event)"
        class="inline-flex items-center gap-2 rounded-xl border border-[var(--site-border)] bg-[color-mix(in_srgb,var(--site-surface),transparent_2%)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--site-text)] transition-all hover:border-[var(--primary-400)] hover:text-[var(--primary-400)]"
      >
        <i class="bi bi-box-arrow-in-right text-sm"></i>
        Login
      </button>

      <a
        routerLink="/visitor-signup"
        class="inline-flex items-center gap-2 rounded-xl border border-[var(--site-border)] bg-[color-mix(in_srgb,var(--site-surface),transparent_2%)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--site-text)] transition-all hover:border-[var(--primary-400)] hover:text-[var(--primary-400)]"
      >
        <i class="bi bi-person-plus-fill text-sm"></i>
        Sign Up
      </a>
    }

    @if (activePublicUser(); as user) {
      <div class="relative inline-flex items-center gap-2">
        <div
          class="inline-flex items-center gap-2 rounded-xl border border-[var(--site-border)] bg-[color-mix(in_srgb,var(--site-surface),transparent_2%)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--site-text)]"
        >
          <i class="bi bi-person-check-fill text-sm text-[var(--primary-400)]"></i>
          {{ getUserGreeting(user) }}
        </div>

        <button
          type="button"
          class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--site-border)] text-[var(--site-text)] hover:border-[var(--primary-400)]"
          (click)="toggleUserMenu($event)"
        >
          <i class="bi bi-chevron-down text-xs"></i>
        </button>

        @if (showUserMenu) {
          <div class="profile-menu">
            <div class="profile-menu__label">Account</div>
            @if (!user.hasPassword) {
              <button type="button" class="profile-menu__item" (click)="onSetPassword($event)">
                Set a password
              </button>
            }
            <button type="button" class="profile-menu__item" (click)="openProfile($event)">
              Profile
            </button>
            @if (canAccessAdminCms()) {
              <a routerLink="/admin" class="profile-menu__item" (click)="showUserMenu = false">
                Admin CMS
              </a>
            }
            <button
              type="button"
              class="profile-menu__item profile-menu__item--logout"
              (click)="onLogout($event)"
            >
              Logout
            </button>
          </div>
        }
      </div>
    }

    <!-- THEME TOGGLE -->
    <div
      class="flex items-center cursor-pointer group"
      (click)="themeService.toggleTheme()"
      role="button"
      tabindex="1"
      [attr.aria-label]="themeService.darkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
    >
      <div
        class="relative flex items-center bg-(--site-surface) border border-(--site-border) rounded-[1.125rem] h-9 w-20 p-[2px] box-border transition-all duration-200 group-hover:border-(--site-text-muted)"
      >
        <div
          class="absolute top-[2px] left-[2px] bottom-[2px] w-[calc(50%-2px)] rounded-2xl bg-[color-mix(in_srgb,var(--site-text),transparent_35%)] shadow-sm z-10 transition-transform duration-300 cubic-[0.2,0.8,0.2,1]"
          [class.translate-x-full]="themeService.darkMode()"
        ></div>
        <div
          class="flex-1 flex items-center justify-center z-20 text-base transition-colors duration-200"
          [style.color]="themeService.darkMode() ? 'var(--site-text-muted)' : 'var(--site-surface)'"
        >
          <i class="bi bi-cloud-sun"></i>
        </div>
        <div
          class="flex-1 flex items-center justify-center z-20 text-base transition-colors duration-200"
          [style.color]="themeService.darkMode() ? 'var(--site-bg)' : 'var(--site-text-muted)'"
        >
          <i class="bi bi-cloud-moon"></i>
        </div>
      </div>
    </div>
  </div>
</header>

@if (showPublicAuthModal) {
  <app-public-auth-modal
    [resetEmail]="resetPasswordEmail"
    [resetToken]="resetPasswordToken"
    (closed)="closePublicAuthModal()"
  />
}

@if (showProfileModal && activePublicUser(); as user) {
  <div class="profile-modal-overlay" (click)="closeProfile()">
    <div
      class="profile-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-modal-title"
      (click)="$event.stopPropagation()"
    >
      <div class="profile-modal__header">
        <div>
          <span class="profile-modal__eyebrow">Account</span>
          <h3 id="profile-modal-title">
            {{ profileEditing ? 'Edit Profile' : 'Visitor Profile' }}
          </h3>
          <p>
            {{
              profileEditing
                ? 'Update your details for future requests.'
                : 'Your account details at a glance.'
            }}
          </p>
        </div>
        <button
          type="button"
          (click)="closeProfile()"
          class="profile-modal__close"
          aria-label="Close profile"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="profile-modal__body">
        @if (!profileEditing) {
          <div class="profile-overview">
            <p><strong>Full Name:</strong> {{ user.fullName || '-' }}</p>
            <p><strong>Email:</strong> {{ user.email || '-' }}</p>
            @if (profileAccount()?.phone) {
              <p><strong>Phone:</strong> {{ profileAccount()?.phone }}</p>
            }
            @if (profileAccount()?.organization) {
              <p><strong>Organization:</strong> {{ profileAccount()?.organization }}</p>
            }
            @if (profileAccount()?.position) {
              <p><strong>Position:</strong> {{ profileAccount()?.position }}</p>
            }
            @if (user.accountStatus === 'normal_user') {
              <p><strong>Purpose:</strong> {{ user.visitPurpose || '-' }}</p>
            }
            @if (user.accountStatus === 'admin' || user.accountStatus === 'super_admin') {
              <p><strong>Access:</strong> All access</p>
            }
            <p>
              <strong>Account Status:</strong>
              {{
                user.accountStatus === 'super_admin'
                  ? 'Superadmin'
                  : user.accountStatus === 'admin'
                    ? 'Admin'
                    : 'Normal User'
              }}
            </p>
          </div>
          <button type="button" class="profile-save profile-edit-button" (click)="editProfile()">
            Edit profile
          </button>
        } @else {
          <div class="profile-form-grid">
            <label class="profile-field profile-field--wide"
              ><span>Full name</span
              ><input
                class="profile-input"
                type="text"
                autocomplete="name"
                [(ngModel)]="profileForm.name"
            /></label>
            <label class="profile-field profile-field--wide"
              ><span>Email</span
              ><input
                class="profile-input"
                type="email"
                autocomplete="email"
                [(ngModel)]="profileForm.email"
            /></label>
            <label class="profile-field"
              ><span>Phone</span
              ><input
                class="profile-input"
                type="tel"
                autocomplete="tel"
                [(ngModel)]="profileForm.phone"
            /></label>
            <label class="profile-field"
              ><span>Organization</span
              ><input
                class="profile-input"
                type="text"
                autocomplete="organization"
                [(ngModel)]="profileForm.organization"
            /></label>
            <label class="profile-field profile-field--wide"
              ><span>Position</span
              ><input
                class="profile-input"
                type="text"
                autocomplete="organization-title"
                [(ngModel)]="profileForm.position"
            /></label>
          </div>
          @if (profileError) {
            <p class="profile-error" role="alert">{{ profileError }}</p>
          }
          <div class="profile-modal__actions">
            <button type="button" class="profile-cancel" (click)="cancelProfileEdit()">
              Cancel
            </button>
            <button
              type="button"
              class="profile-save"
              [disabled]="profileSaving"
              (click)="saveProfile()"
            >
              {{ profileSaving ? 'Saving…' : 'Save changes' }}
            </button>
          </div>
        }
      </div>
    </div>
  </div>
}

@if (showLogoutModal) {
  <div
    class="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
    (click)="closeLogoutModal()"
  >
    <div
      class="w-full max-w-md rounded-[1.75rem] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 shadow-[0_30px_80px_rgba(0,0,0,0.28)]"
      (click)="$event.stopPropagation()"
    >
      <div
        class="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-amber-400/45 bg-amber-500/10 text-3xl text-amber-500"
      >
        <i class="bi bi-exclamation-lg"></i>
      </div>
      <div class="mt-5 text-center">
        <h3 class="text-2xl font-bold text-[var(--site-text)]">Logout</h3>
        <p class="mt-2 text-sm leading-6 text-[var(--site-text-muted)]">
          Are you sure you want to logout?
        </p>
      </div>
      <div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
        <button
          type="button"
          (click)="closeLogoutModal()"
          class="inline-flex items-center justify-center rounded-xl border border-[var(--site-border)] bg-[color-mix(in_srgb,var(--site-surface),white_4%)] px-5 py-3 text-sm font-semibold text-[var(--site-text)] transition-colors hover:border-[var(--primary-400)]"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="confirmLogout()"
          class="inline-flex items-center justify-center rounded-xl bg-[linear-gradient(135deg,#ef4444,color-mix(in_srgb,#ef4444,black_14%))] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/20 transition-opacity hover:opacity-95"
        >
          Yes, logout
        </button>
      </div>
    </div>
  </div>
}
